import type { CheckMode } from './check-mode';

/**
 * Isi kartu "Status sesi" pada beranda — MURNI.
 *
 * ADA KARENA sapuan Chrome 22 Agustus 2026 menemukan bahwa kartu terbesar di beranda kurir
 * berbunyi *"Status sesi belum dapat dibaca dari server"* — SETIAP HARI, bukan sesekali.
 *
 * Sebabnya bukan server yang bermasalah. Beranda menyimpulkan keadaan sesi dari
 * `GET /api/attendance/me`, dan endpoint itu mengembalikan `AttendanceDay[]` telanjang — tidak
 * pernah membawa event. `eventsAvailable` karenanya SELALU palsu, sehingga cabang "tidak dapat
 * dibaca" adalah keadaan normal, bukan keadaan darurat. Peringatan yang selalu menyala berhenti
 * menjadi peringatan; ia hanya membuat kurir mengira aplikasinya rusak pada pagi pertamanya.
 *
 * Endpoint yang benar sudah ada dan sudah dipakai layar Check In sejak pilot 16 Agustus:
 * `GET /api/me/attendance/today` menjawab `nextAction` langsung dari kebijakan organisasi.
 * Komentar di `client.ts` bahkan sudah mencatat bahwa cara lama "lahir ketika endpoint ini belum
 * ada, dan bertahan setelah ia ada" — beranda hanya tidak pernah ikut dipindahkan.
 *
 * Aturan yang diwarisi utuh dari `resolveCheckMode`: **klien tidak menebak.** `unavailable` tetap
 * dinyatakan apa adanya, karena menawarkan Check In kepada orang yang sesinya masih terbuka
 * membuat tombolnya ditolak server tanpa penjelasan.
 */

export interface HomeSessionCard {
  /** Kalimat keadaan. Selalu ada — kartu tanpa kalimat terbaca seperti gagal memuat. */
  readonly kalimat: string;
  /** Benar bila kalimatnya menuntut perhatian, bukan sekadar melaporkan. */
  readonly menonjol: boolean;
  /** Label tombol besar. */
  readonly tombol: string;
}

/**
 * @param mode      hasil `resolveCheckMode(nextAction)`, atau `checkModeOnError()` bila gagal.
 * @param terbukaDariHariSebelumnya `openSessionStartedOnEarlierDay` dari server.
 * @param gagalMemuat benar HANYA bila permintaannya sendiri gagal — dibedakan dari
 *                    "tidak ada kebijakan aktif", meski keduanya berakhir `unavailable`.
 */
export function homeSessionCard(
  mode: CheckMode,
  terbukaDariHariSebelumnya: boolean,
  gagalMemuat: boolean,
): HomeSessionCard {
  if (mode === 'CHECK_OUT') {
    return {
      /*
       * Sesi lintas tengah malam disebut LEBIH DULU dan terpisah: ia menuntut tindakan berbeda
       * (absen pulang dulu, dan tercatat pada hari kerja kemarin), dan menyamakannya dengan sesi
       * biasa membuat orang mengira ia sedang bekerja pada hari ini.
       */
      kalimat: terbukaDariHariSebelumnya
        ? 'Sesi Anda masih terbuka sejak hari kerja sebelumnya. Lakukan absen pulang lebih dulu.'
        : 'Sesi Anda sedang terbuka — Anda sudah absen masuk hari ini.',
      menonjol: terbukaDariHariSebelumnya,
      tombol: 'Absen pulang',
    };
  }

  if (mode === 'CHECK_IN') {
    return {
      kalimat: 'Anda belum absen masuk hari ini.',
      menonjol: false,
      tombol: 'Absen masuk',
    };
  }

  /*
   * `unavailable`. Dua sebab yang berbeda, dan keduanya HARUS berbunyi berbeda: kebijakan yang
   * belum aktif adalah keadaan organisasi yang tidak dapat diperbaiki kurir sendiri, sedangkan
   * permintaan yang gagal biasanya sinyal — dan menyuruh orang menghubungi admin karena sinyalnya
   * buruk mengirimnya ke tempat yang salah.
   */
  return gagalMemuat
    ? {
        kalimat:
          'Status sesi belum dapat dibaca — periksa koneksi Anda. Layar Absen menanyakan ulang langsung ke server.',
        menonjol: true,
        tombol: 'Buka layar Absen',
      }
    : {
        kalimat:
          'Organisasi Anda belum mengaktifkan kebijakan absensi, jadi absen masuk belum dapat dilakukan.',
        menonjol: true,
        tombol: 'Buka layar Absen',
      };
}

/**
 * Benar bila tombol absen MASUK harus disembunyikan karena hari ini bukan hari kerja (item 8,
 * review 12 Sep 2026) — PEMBALIKAN keputusan sebelumnya, lihat catatan panjang di
 * `attendance-policy.ts` (API).
 *
 * HANYA berlaku untuk `mode === 'CHECK_IN'` — absen pulang (K2 & ADR-0034 §5) tidak pernah
 * ditahan oleh gerbang ini, persis seperti server (`attendance.service.ts`, gerbang yang sama
 * TIDAK PERNAH dipanggil di jalur `performCheckOut`).
 */
export function homeCheckInGatedByNonWorkDay(
  mode: CheckMode,
  hariIniLibur: boolean,
  allowCheckInOnNonWorkDays: boolean,
): boolean {
  return mode === 'CHECK_IN' && hariIniLibur && !allowCheckInOnNonWorkDays;
}
